import { z } from 'zod';
import { uuidSchema, shortString, positiveInt } from './validation.js';
/**
 * Location Schemas
 *
 * Shared validation schemas for location management operations.
 * All locations are organization-scoped for multi-tenant isolation.
 */
/**
 * Create a new location
 */
export const createLocationSchema = z.object({
    name: shortString.min(1, 'Location name is required'),
    shopify_location_id: positiveInt,
    city: shortString.min(1, 'City is required'),
    country_code: z.string().length(2, 'Country code must be 2 characters (e.g., AU, US)'),
    timezone: shortString.min(1, 'Timezone is required'),
    is_active: z.boolean().optional(),
    enable_auto_availability: z.boolean().optional(),
    address_line1: z.string().trim().max(200).optional().nullable(),
    address_line2: z.string().trim().max(200).optional().nullable(),
    state_province: z.string().trim().max(120).optional().nullable(),
    postal_code: z.string().trim().max(60).optional().nullable(),
    contact_emails: z.array(z.string().trim().email().max(254)).max(20).optional(),
    contact_phone: z.string().trim().max(60).optional().nullable(),
    customs_info: z.string().trim().max(2000).optional().nullable(),
});
/**
 * Update an existing location
 */
export const updateLocationSchema = z.object({
    locationId: uuidSchema,
    updates: z.object({
        name: shortString.min(1).optional(),
        shopify_location_id: positiveInt.optional(),
        city: shortString.min(1).optional(),
        country_code: z.string().length(2).optional(),
        timezone: shortString.min(1).optional(),
        is_active: z.boolean().optional(),
        enable_auto_availability: z.boolean().optional(),
        address_line1: z.string().trim().max(200).optional().nullable(),
        address_line2: z.string().trim().max(200).optional().nullable(),
        state_province: z.string().trim().max(120).optional().nullable(),
        postal_code: z.string().trim().max(60).optional().nullable(),
        contact_emails: z.array(z.string().trim().email().max(254)).max(20).optional(),
        contact_phone: z.string().trim().max(60).optional().nullable(),
        customs_info: z.string().trim().max(2000).optional().nullable(),
    }),
});
/**
 * Message marker of the CONFLICT thrown when a create or rename hits the
 * "one location name per organization" rule. tRPC drops error causes, so the
 * message STARTS with `${LOCATION_NAME_TAKEN_MARKER}:` and the location form
 * branches on that prefix, strips it, and shows the rest under the Name field.
 */
export const LOCATION_NAME_TAKEN_MARKER = 'LOCATION_NAME_TAKEN';
/**
 * Message marker of the BAD_REQUEST thrown when a create or edit would give
 * one of the org's own locations a Shopify location that is already connected
 * to a partner warehouse. Same contract as LOCATION_NAME_TAKEN_MARKER: the
 * form strips the prefix and shows the rest under the Shopify location field.
 */
export const SHOPIFY_LOCATION_CONNECTED_TO_3PL_MARKER = 'SHOPIFY_LOCATION_CONNECTED_TO_3PL';
/**
 * Get locations for organization
 */
export const getLocationsSchema = z.object({
    includeInactive: z.boolean().optional(),
    organizationId: uuidSchema.optional(),
});
/**
 * Deactivate a location
 */
export const deleteLocationSchema = z.object({
    locationId: uuidSchema,
});
